// src/pages/AgencyReports.jsx
// Agency Reports — Bloomberg-terminal style intelligence layer
// Production, Retention, Agent Performance, Commission, Book of Business, War Room

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  BarChart3, TrendingUp, TrendingDown, Users, DollarSign, AlertTriangle,
  Shield, ChevronRight, ChevronDown, RefreshCw, Download, BookOpen,
  Maximize2, Minimize2, Award, Target, Activity, Layers, FileText,
  ArrowUpRight, ArrowDownRight, Minus, Zap, Calendar, Filter, Search,
  CheckCircle, Clock, X,
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------
const CHART_COLORS = ['#F59E0B','#3B82F6','#10B981','#EF4444','#8B5CF6','#EC4899','#14B8A6','#F97316'];
const CARD = 'bg-gray-900 border border-gray-800 rounded-lg';
const KPI_NUMBER = 'font-mono tabular-nums';

// ---------------------------------------------------------------------------
// Quick-select date ranges
// ---------------------------------------------------------------------------
const QUICK_RANGES = [
  { label: '7D',     days: 7 },
  { label: '30D',    days: 30 },
  { label: '90D',    days: 90 },
  { label: 'Last AEP', custom: true, id: 'aep' },
  { label: 'Custom', custom: true, id: 'custom' },
];

function getRange(option) {
  const now = new Date();
  if (option.days) {
    const start = new Date(now);
    start.setDate(now.getDate() - option.days);
    return { start: start.toISOString().slice(0, 10), end: now.toISOString().slice(0, 10) };
  }
  if (option.id === 'aep') {
    const yr = now.getMonth() >= 9 ? now.getFullYear() : now.getFullYear() - 1;
    return { start: `${yr}-10-15`, end: `${yr}-12-07` };
  }
  // custom — handled by caller
  return null;
}

// ---------------------------------------------------------------------------
// Utility components
// ---------------------------------------------------------------------------
function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <RefreshCw size={24} className="text-amber-400 animate-spin" />
    </div>
  );
}

function Delta({ pct, size = 'sm' }) {
  if (pct === null || pct === undefined) return null;
  const pos   = pct > 0;
  const zero  = pct === 0;
  const color = zero ? 'text-gray-400' : pos ? 'text-emerald-400' : 'text-red-400';
  const Icon  = zero ? Minus : pos ? ArrowUpRight : ArrowDownRight;
  const fs    = size === 'lg' ? 'text-base' : 'text-xs';
  return (
    <span className={`inline-flex items-center gap-0.5 ${color} ${fs} font-mono`}>
      <Icon size={size === 'lg' ? 16 : 12} />
      {pct !== 0 && `${Math.abs(pct)}%`}
    </span>
  );
}

function KpiTile({ label, value, delta, sub, icon: Icon, color = 'amber', loading }) {
  const accentColor = {
    amber:   'text-amber-400',
    emerald: 'text-emerald-400',
    red:     'text-red-400',
    blue:    'text-blue-400',
    purple:  'text-purple-400',
  }[color] || 'text-amber-400';

  return (
    <div className={`${CARD} p-4 flex flex-col gap-1`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">{label}</span>
        {Icon && <Icon size={16} className="text-gray-600" />}
      </div>
      {loading ? (
        <div className="h-8 w-24 bg-gray-800 rounded animate-pulse mt-1" />
      ) : (
        <>
          <div className={`text-3xl font-bold ${KPI_NUMBER} ${accentColor}`}>{value}</div>
          <div className="flex items-center gap-2 mt-0.5">
            {delta !== undefined && <Delta pct={delta} />}
            {sub && <span className="text-xs text-gray-500">{sub}</span>}
          </div>
        </>
      )}
    </div>
  );
}

function NarrativeBar({ text, loading }) {
  if (loading) return (
    <div className="bg-gray-900 border border-amber-400/20 rounded-lg p-3 mb-4">
      <div className="h-4 bg-gray-800 rounded animate-pulse w-3/4" />
    </div>
  );
  if (!text) return null;
  return (
    <div className="bg-gray-900 border border-amber-400/20 rounded-lg p-3 mb-4">
      <div className="flex items-start gap-2">
        <Zap size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-gray-300 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function SectionHeader({ title, subtitle, onExport, exportReport }) {
  return (
    <div className="flex items-start justify-between mb-4">
      <div>
        <h2 className="text-lg font-bold text-white">{title}</h2>
        {subtitle && <p className="text-sm text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {onExport && (
        <button
          onClick={() => onExport(exportReport)}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-amber-400 transition-colors border border-gray-700 hover:border-amber-400/50 rounded px-2 py-1"
        >
          <Download size={12} />
          CSV
        </button>
      )}
    </div>
  );
}

const CustomTooltipStyle = {
  backgroundColor: '#111827',
  border: '1px solid #374151',
  borderRadius: 6,
  color: '#fff',
  fontSize: 12,
  padding: '8px 12px',
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={CustomTooltipStyle}>
      <p className="font-medium text-gray-300 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar nav items
// ---------------------------------------------------------------------------
const NAV_ITEMS = [
  { id: 'overview',    label: 'Dashboard Overview',      icon: LayoutDashboard2 },
  {
    id: 'production', label: 'Production', icon: TrendingUp, children: [
      { id: 'prod-enrollments', label: 'Enrollment by Period' },
      { id: 'prod-by-agent',    label: 'Enrollment by Agent' },
      { id: 'prod-by-carrier',  label: 'Enrollment by Carrier' },
      { id: 'prod-aep-oep',     label: 'AEP / OEP Comparison' },
    ],
  },
  {
    id: 'retention', label: 'Retention', icon: Shield, children: [
      { id: 'ret-pipeline', label: 'Renewal Pipeline' },
      { id: 'ret-churn',    label: 'Churn Analysis' },
    ],
  },
  {
    id: 'agents', label: 'Agent Performance', icon: Users, children: [
      { id: 'agt-scorecards',  label: 'Agent Scorecards' },
      { id: 'agt-leaderboard', label: 'Leaderboard' },
    ],
  },
  {
    id: 'commission', label: 'Commission', icon: DollarSign, children: [
      { id: 'comm-earned',  label: 'Earned by Period' },
      { id: 'comm-missing', label: 'Missing Commission Detector' },
    ],
  },
  { id: 'book',     label: 'Book of Business', icon: BookOpen },
  { id: 'war-room', label: 'War Room Mode',     icon: Maximize2 },
];

function LayoutDashboard2(props) {
  return <BarChart3 {...props} />;
}

// ---------------------------------------------------------------------------
// Date Range Controls
// ---------------------------------------------------------------------------
function DateRangeBar({ range, onRangeChange, activeQuick, onQuickSelect }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {QUICK_RANGES.map(q => (
        <button
          key={q.label}
          onClick={() => onQuickSelect(q)}
          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
            activeQuick === q.label
              ? 'bg-amber-400 text-gray-900'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          {q.label}
        </button>
      ))}
      {activeQuick === 'Custom' && (
        <>
          <input
            type="date"
            value={range.start}
            onChange={e => onRangeChange({ ...range, start: e.target.value })}
            className="bg-gray-800 border border-gray-700 text-white text-xs rounded px-2 py-1"
          />
          <span className="text-gray-500 text-xs">to</span>
          <input
            type="date"
            value={range.end}
            onChange={e => onRangeChange({ ...range, end: e.target.value })}
            className="bg-gray-800 border border-gray-700 text-white text-xs rounded px-2 py-1"
          />
        </>
      )}
    </div>
  );
}

// ===========================================================================
// MAIN COMPONENT
// ===========================================================================
export default function AgencyReports() {
  const [activeView, setActiveView] = useState('overview');
  const [expanded, setExpanded]     = useState({ production: true, retention: false, agents: false, commission: false });
  const [range, setRange]           = useState({ start: '', end: '' });
  const [activeQuick, setActiveQuick] = useState('30D');
  const [warRoomMode, setWarRoomMode] = useState(false);
  const [warRoomWindow, setWarRoomWindow] = useState('aep');

  // Initialize with 30D
  useEffect(() => {
    const r = getRange({ days: 30 });
    setRange(r);
  }, []);

  const handleQuickSelect = useCallback(q => {
    setActiveQuick(q.label);
    if (q.id !== 'custom') {
      const r = getRange(q);
      if (r) setRange(r);
    }
  }, []);

  const handleExportCsv = useCallback(report => {
    const params = new URLSearchParams({
      report,
      start: range.start,
      end: range.end,
    });
    window.open(`${BASE}/api/reports/export/csv?${params.toString()}`, '_blank');
  }, [range]);

  const setView = useCallback(id => {
    setActiveView(id);
    // Auto-expand parent
    if (id.startsWith('prod'))  setExpanded(e => ({ ...e, production: true }));
    if (id.startsWith('ret'))   setExpanded(e => ({ ...e, retention: true }));
    if (id.startsWith('agt'))   setExpanded(e => ({ ...e, agents: true }));
    if (id.startsWith('comm'))  setExpanded(e => ({ ...e, commission: true }));
  }, []);

  // War Room is a special full-screen view
  if (warRoomMode) {
    return (
      <WarRoom
        window={warRoomWindow}
        onExit={() => setWarRoomMode(false)}
        onWindowChange={setWarRoomWindow}
      />
    );
  }

  return (
    <div className="flex h-full bg-gray-950 text-white min-h-screen">
      {/* Left Sidebar */}
      <aside className="w-56 flex-shrink-0 bg-gray-900 border-r border-gray-800 flex flex-col overflow-y-auto">
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-amber-400" />
            <span className="font-bold text-white text-sm">Reports</span>
          </div>
        </div>
        <nav className="flex-1 py-2">
          {NAV_ITEMS.map(item => (
            <NavSection
              key={item.id}
              item={item}
              activeView={activeView}
              expanded={expanded}
              setExpanded={setExpanded}
              onSelect={setView}
            />
          ))}
        </nav>
        <div className="p-3 border-t border-gray-800">
          <button
            onClick={() => setWarRoomMode(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded bg-amber-400/10 hover:bg-amber-400/20 text-amber-400 text-xs font-medium transition-colors"
          >
            <Zap size={14} />
            War Room Mode
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Date range bar (shown on most views) */}
          {activeView !== 'war-room' && (
            <div className="mb-5 flex flex-wrap items-center gap-4">
              <DateRangeBar
                range={range}
                onRangeChange={setRange}
                activeQuick={activeQuick}
                onQuickSelect={handleQuickSelect}
              />
            </div>
          )}

          {/* View Router */}
          {activeView === 'overview'        && <ViewOverview range={range} onExport={handleExportCsv} />}
          {activeView === 'prod-enrollments'&& <ViewEnrollmentsByPeriod range={range} onExport={handleExportCsv} />}
          {activeView === 'prod-by-agent'   && <ViewByAgent range={range} onExport={handleExportCsv} />}
          {activeView === 'prod-by-carrier' && <ViewByCarrier range={range} onExport={handleExportCsv} />}
          {activeView === 'prod-aep-oep'    && <ViewAepOep />}
          {activeView === 'ret-pipeline'    && <ViewRenewalPipeline range={range} onExport={handleExportCsv} />}
          {activeView === 'ret-churn'       && <ViewChurn range={range} onExport={handleExportCsv} />}
          {activeView === 'agt-scorecards'  && <ViewScorecards range={range} onExport={handleExportCsv} />}
          {activeView === 'agt-leaderboard' && <ViewLeaderboard range={range} onExport={handleExportCsv} />}
          {activeView === 'comm-missing'    && <ViewMissingCommission onExport={handleExportCsv} />}
          {activeView === 'comm-earned'     && <ViewCommissionEarned range={range} onExport={handleExportCsv} />}
          {activeView === 'book'            && <ViewBookOfBusiness />}
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar nav section component
// ---------------------------------------------------------------------------
function NavSection({ item, activeView, expanded, setExpanded, onSelect }) {
  const isLeaf     = !item.children;
  const isActive   = activeView === item.id;
  const isOpen     = expanded[item.id];
  const Icon       = item.icon;
  const hasActive  = item.children?.some(c => c.id === activeView);

  if (isLeaf) {
    return (
      <button
        onClick={() => onSelect(item.id)}
        className={`w-full flex items-center gap-2 px-4 py-2 text-xs transition-colors ${
          isActive
            ? 'text-amber-400 bg-amber-400/10 border-r-2 border-amber-400'
            : 'text-gray-400 hover:text-white hover:bg-gray-800'
        }`}
      >
        {Icon && <Icon size={14} />}
        {item.label}
      </button>
    );
  }

  return (
    <div>
      <button
        onClick={() => setExpanded(e => ({ ...e, [item.id]: !e[item.id] }))}
        className={`w-full flex items-center gap-2 px-4 py-2 text-xs transition-colors ${
          hasActive ? 'text-white' : 'text-gray-400 hover:text-white'
        } hover:bg-gray-800`}
      >
        {Icon && <Icon size={14} className={hasActive ? 'text-amber-400' : ''} />}
        <span className="flex-1 text-left font-medium">{item.label}</span>
        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {isOpen && (
        <div className="pl-4">
          {item.children.map(child => (
            <button
              key={child.id}
              onClick={() => onSelect(child.id)}
              className={`w-full flex items-center gap-2 px-4 py-1.5 text-xs transition-colors ${
                activeView === child.id
                  ? 'text-amber-400 bg-amber-400/10 border-r-2 border-amber-400'
                  : 'text-gray-500 hover:text-gray-200 hover:bg-gray-800'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
              {child.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// VIEW: Dashboard Overview
// ===========================================================================
function ViewOverview({ range, onExport }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/reports/overview`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div>
      <div className="mb-6">
        <p className="text-gray-400 text-sm">{today}</p>
        <h1 className="text-2xl font-bold text-white">Agency Overview</h1>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="New Clients (MTD)"   value={data?.kpis?.newClients ?? '—'} delta={data?.kpis?.newClientsVsLast} sub="vs last month" icon={TrendingUp} color="amber"   loading={loading} />
        <KpiTile label="Renewals Due (30D)"  value={data?.kpis?.renewalsDue30 ?? '—'} sub="upcoming" icon={Calendar} color="blue"    loading={loading} />
        <KpiTile label="Churned (MTD)"       value={data?.kpis?.churnedThisMonth ?? '—'} sub="this month" icon={TrendingDown} color="red"     loading={loading} />
        <KpiTile label="Active Clients"      value={data?.kpis?.activeClients ?? '—'} sub="total book" icon={Users} color="emerald" loading={loading} />
      </div>

      {/* Alerts bar */}
      {!loading && data?.alerts?.missingCommCount > 0 && (
        <div className="bg-amber-400/10 border border-amber-400/30 rounded-lg p-3 mb-6 flex items-center gap-3">
          <AlertTriangle size={16} className="text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-300">
            {data.alerts.missingCommCount} potential missing commission entr{data.alerts.missingCommCount === 1 ? 'y' : 'ies'} detected
            {data.alerts.missingCommAmt > 0 && ` — est. $${Math.round(data.alerts.missingCommAmt).toLocaleString()} at risk`}.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Enrollment Trend */}
        <div className={`${CARD} p-4 lg:col-span-2`}>
          <h3 className="text-sm font-semibold text-white mb-4">Enrollment Trend (12 months)</h3>
          {loading ? <Spinner /> : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data?.enrollTrend || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="enrollGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#F59E0B" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 10 }} tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="cnt" name="Enrollments" stroke="#F59E0B" fill="url(#enrollGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Carrier Mix */}
        <div className={`${CARD} p-4`}>
          <h3 className="text-sm font-semibold text-white mb-4">Carrier Mix (Active Book)</h3>
          {loading ? <Spinner /> : (
            <>
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie
                    data={data?.carrierMix || []}
                    dataKey="cnt"
                    nameKey="carrier"
                    cx="50%" cy="50%"
                    outerRadius={60}
                    innerRadius={35}
                  >
                    {(data?.carrierMix || []).map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {(data?.carrierMix || []).slice(0, 5).map((c, i) => (
                  <div key={c.carrier} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className="text-gray-300 truncate max-w-[100px]">{c.carrier}</span>
                    </div>
                    <span className="text-gray-400 font-mono">{c.cnt}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Top Agents */}
        <div className={`${CARD} p-4`}>
          <h3 className="text-sm font-semibold text-white mb-4">Top Agents (MTD)</h3>
          {loading ? <Spinner /> : (
            <div className="space-y-2">
              {(data?.topAgents || []).map((a, i) => (
                <div key={a.id} className="flex items-center gap-3">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    i === 0 ? 'bg-amber-400 text-gray-900' :
                    i === 1 ? 'bg-gray-300 text-gray-900' :
                    i === 2 ? 'bg-amber-700 text-white' : 'bg-gray-700 text-gray-300'
                  }`}>{i + 1}</span>
                  <span className="flex-1 text-sm text-gray-200 truncate">{a.name}</span>
                  <span className="font-mono text-sm text-amber-400 font-bold">{a.enrollments}</span>
                </div>
              ))}
              {!data?.topAgents?.length && <p className="text-xs text-gray-500">No enrollment data for this period.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// VIEW: Enrollments by Period
// ===========================================================================
function ViewEnrollmentsByPeriod({ range, onExport }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState('month');

  const load = useCallback(async () => {
    if (!range.start) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ start: range.start, end: range.end, groupBy });
      const res  = await fetch(`${BASE}/api/reports/production/enrollments?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [range, groupBy]);

  useEffect(() => { load(); }, [load]);

  // Aggregate trend rows into a flat array (group carriers together per period)
  const chartData = React.useMemo(() => {
    if (!data?.trend) return [];
    const periodMap = {};
    data.trend.forEach(r => {
      if (!periodMap[r.period]) periodMap[r.period] = { period: r.period };
      periodMap[r.period][r.carrier || 'Unknown'] = (periodMap[r.period][r.carrier || 'Unknown'] || 0) + parseInt(r.enrollments);
    });
    return Object.values(periodMap).sort((a, b) => a.period.localeCompare(b.period));
  }, [data]);

  const carriers = React.useMemo(() => {
    if (!data?.trend) return [];
    return [...new Set(data.trend.map(r => r.carrier || 'Unknown'))];
  }, [data]);

  return (
    <div>
      <SectionHeader
        title="Enrollment by Period"
        subtitle={`${range.start} to ${range.end}`}
        onExport={onExport}
        exportReport="enrollments"
      />
      <NarrativeBar text={data?.narrative} loading={loading} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total Enrollments" value={data?.summary?.total ?? '—'} delta={data?.summary?.delta} sub="vs prior period" icon={TrendingUp} loading={loading} />
        <KpiTile label="Prior Period"       value={data?.summary?.priorTotal ?? '—'} icon={Activity}  color="blue"    loading={loading} />
        <KpiTile label="Active Agents"      value={data?.summary?.agents_active ?? '—'} icon={Users} color="emerald" loading={loading} />
        <KpiTile label="Carriers Used"      value={data?.summary?.carriers ?? '—'} icon={Layers}    color="purple"  loading={loading} />
      </div>

      {/* Group By Toggle */}
      <div className="flex gap-2 mb-4">
        {['week','month','quarter'].map(g => (
          <button key={g} onClick={() => setGroupBy(g)}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              groupBy === g ? 'bg-amber-400 text-gray-900' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}>
            {g.charAt(0).toUpperCase() + g.slice(1)}
          </button>
        ))}
      </div>

      <div className={`${CARD} p-4 mb-6`}>
        {loading ? <Spinner /> : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="period" tick={{ fill: '#6B7280', fontSize: 10 }}
                tickFormatter={v => v ? v.slice(5) || v.slice(0, 7) : v} />
              <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#9CA3AF' }} />
              {carriers.slice(0, 6).map((c, i) => (
                <Bar key={c} dataKey={c} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Data table */}
      <div className={`${CARD} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left p-3 text-gray-400 font-medium">Period</th>
                <th className="text-right p-3 text-gray-400 font-medium">Enrollments</th>
                <th className="text-left p-3 text-gray-400 font-medium">Carrier</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} className="p-3"><Spinner /></td></tr>
              ) : data?.trend?.length ? (
                data.trend.map((r, i) => (
                  <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="p-3 font-mono text-gray-300">{r.period}</td>
                    <td className="p-3 font-mono text-right text-amber-400">{r.enrollments}</td>
                    <td className="p-3 text-gray-400">{r.carrier || 'Unknown'}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={3} className="p-3 text-center text-gray-500">No data for this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// VIEW: Enrollment by Agent
// ===========================================================================
function ViewByAgent({ range, onExport }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!range.start) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ start: range.start, end: range.end });
      const res  = await fetch(`${BASE}/api/reports/production/by-agent?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <SectionHeader title="Enrollment by Agent" subtitle={`${range.start} to ${range.end}`} onExport={onExport} exportReport="by-agent" />
      <NarrativeBar text={data?.narrative} loading={loading} />

      <div className={`${CARD} p-4 mb-6`}>
        {loading ? <Spinner /> : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={data?.agents || []}
              layout="vertical"
              margin={{ top: 5, right: 40, left: 80, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis type="number" tick={{ fill: '#6B7280', fontSize: 10 }} />
              <YAxis type="category" dataKey="agent_name" tick={{ fill: '#9CA3AF', fontSize: 11 }} width={75} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="enrollments" name="Enrollments" fill="#F59E0B" radius={[0, 3, 3, 0]} />
              <Bar dataKey="priorEnrollments" name="Prior Period" fill="#374151" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className={`${CARD} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left p-3 text-gray-400 font-medium">Agent</th>
                <th className="text-right p-3 text-gray-400 font-medium">Enrollments</th>
                <th className="text-right p-3 text-gray-400 font-medium">vs Prior</th>
                <th className="text-left p-3 text-gray-400 font-medium">Top Carrier</th>
                <th className="text-right p-3 text-gray-400 font-medium">Carriers</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5}><Spinner /></td></tr>
              ) : data?.agents?.length ? (
                data.agents.map((a, i) => (
                  <tr key={a.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="p-3 flex items-center gap-2">
                      <span className={`w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center ${
                        i === 0 ? 'bg-amber-400 text-gray-900' : 'bg-gray-700 text-gray-400'
                      }`}>{i + 1}</span>
                      <span className="text-gray-200">{a.agent_name}</span>
                    </td>
                    <td className="p-3 font-mono text-right text-amber-400 font-bold">{a.enrollments}</td>
                    <td className="p-3 text-right"><Delta pct={a.delta} /></td>
                    <td className="p-3 text-gray-400">{a.top_carrier || '—'}</td>
                    <td className="p-3 font-mono text-right text-gray-400">{a.carriers_worked}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={5} className="p-3 text-center text-gray-500">No data.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// VIEW: Enrollment by Carrier
// ===========================================================================
function ViewByCarrier({ range, onExport }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!range.start) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ start: range.start, end: range.end });
      const res  = await fetch(`${BASE}/api/reports/production/by-carrier?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <SectionHeader title="Enrollment by Carrier" subtitle={`${range.start} to ${range.end}`} onExport={onExport} exportReport="by-carrier" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className={`${CARD} p-4`}>
          {loading ? <Spinner /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={data?.carriers || []}
                  dataKey="enrollments"
                  nameKey="carrier"
                  cx="50%" cy="50%"
                  outerRadius={100}
                  innerRadius={50}
                  label={({ carrier, pct }) => `${carrier} ${pct}%`}
                  labelLine={false}
                >
                  {(data?.carriers || []).map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={`${CARD} p-4`}>
          <h3 className="text-sm font-semibold text-white mb-3">Carrier Breakdown</h3>
          {loading ? <Spinner /> : (
            <div className="space-y-2">
              {(data?.carriers || []).map((c, i) => (
                <div key={c.carrier} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="text-gray-200">{c.carrier}</span>
                      <span className="font-mono text-amber-400">{c.enrollments}</span>
                    </div>
                    <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${c.pct}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 w-10 text-right font-mono">{c.pct}%</span>
                </div>
              ))}
              {!data?.carriers?.length && <p className="text-xs text-gray-500">No data for this period.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// VIEW: AEP / OEP Comparison
// ===========================================================================
function ViewAepOep() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const now = new Date();
  const [aepYear, setAepYear] = useState(now.getFullYear() - 1);
  const [oepYear, setOepYear] = useState(now.getFullYear());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ aepYear, oepYear });
      const res  = await fetch(`${BASE}/api/reports/production/aep-oep?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [aepYear, oepYear]);

  useEffect(() => { load(); }, [load]);

  const aepTotal = data?.aep?.rows?.reduce((s, r) => s + parseInt(r.enrollments), 0) || 0;
  const oepTotal = data?.oep?.rows?.reduce((s, r) => s + parseInt(r.enrollments), 0) || 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-white">AEP / OEP Comparison</h2>
          <p className="text-sm text-gray-400">Side-by-side enrollment period analysis</p>
        </div>
        <div className="flex gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">AEP Year</span>
            <select value={aepYear} onChange={e => setAepYear(parseInt(e.target.value))}
              className="bg-gray-800 border border-gray-700 text-white text-xs rounded px-2 py-1">
              {[now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear()].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">OEP Year</span>
            <select value={oepYear} onChange={e => setOepYear(parseInt(e.target.value))}
              className="bg-gray-800 border border-gray-700 text-white text-xs rounded px-2 py-1">
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${CARD} p-4`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">AEP {aepYear}</h3>
            <span className="text-xs text-gray-400">{data?.aep?.window?.start} – {data?.aep?.window?.end}</span>
          </div>
          <div className="text-3xl font-bold font-mono text-amber-400 mb-4">{aepTotal}</div>
          {loading ? <Spinner /> : (
            <div className="space-y-1.5">
              {(data?.aep?.rows || []).slice(0, 8).map((r, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-gray-300">{r.carrier || 'Unknown'} — {r.plan_type || 'N/A'}</span>
                  <span className="font-mono text-amber-400">{r.enrollments}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={`${CARD} p-4`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">OEP {oepYear}</h3>
            <span className="text-xs text-gray-400">{data?.oep?.window?.start} – {data?.oep?.window?.end}</span>
          </div>
          <div className="text-3xl font-bold font-mono text-blue-400 mb-4">{oepTotal}</div>
          {loading ? <Spinner /> : (
            <div className="space-y-1.5">
              {(data?.oep?.rows || []).slice(0, 8).map((r, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-gray-300">{r.carrier || 'Unknown'} — {r.plan_type || 'N/A'}</span>
                  <span className="font-mono text-blue-400">{r.enrollments}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// VIEW: Renewal Pipeline
// ===========================================================================
function ViewRenewalPipeline({ range, onExport }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [daysAhead, setDaysAhead] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ daysAhead });
      const res  = await fetch(`${BASE}/api/reports/retention/renewal-pipeline?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [daysAhead]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-white">Renewal Pipeline</h2>
          <p className="text-sm text-gray-400">Clients approaching their enrollment anniversary</p>
        </div>
        <div className="flex gap-2">
          {[30, 60, 90].map(d => (
            <button key={d} onClick={() => setDaysAhead(d)}
              className={`px-3 py-1 rounded text-xs font-medium ${
                daysAhead === d ? 'bg-amber-400 text-gray-900' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}>
              {d}D
            </button>
          ))}
        </div>
      </div>

      <KpiTile label={`Renewals Due (${daysAhead}D)`} value={data?.clients?.length ?? '—'} sub="action needed" icon={Clock} color="amber" loading={loading} />

      <div className={`${CARD} overflow-hidden mt-4`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left p-3 text-gray-400 font-medium">Client</th>
                <th className="text-left p-3 text-gray-400 font-medium">Carrier</th>
                <th className="text-left p-3 text-gray-400 font-medium">Plan</th>
                <th className="text-right p-3 text-gray-400 font-medium">Anniversary</th>
                <th className="text-left p-3 text-gray-400 font-medium">Agent</th>
                <th className="text-right p-3 text-gray-400 font-medium">Last Contact</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6}><Spinner /></td></tr>
              ) : data?.clients?.length ? (
                data.clients.map(c => {
                  const anniversary = c.next_anniversary
                    ? new Date(c.next_anniversary).toLocaleDateString() : '—';
                  const lastContact = c.last_contact_date
                    ? new Date(c.last_contact_date).toLocaleDateString() : 'Never';
                  const noContact = !c.last_contact_date;
                  return (
                    <tr key={c.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                      <td className="p-3 text-gray-200">{c.first_name} {c.last_name}</td>
                      <td className="p-3 text-gray-400">{c.carrier || '—'}</td>
                      <td className="p-3 text-gray-400">{c.plan_type || '—'}</td>
                      <td className="p-3 font-mono text-right text-amber-400">{anniversary}</td>
                      <td className="p-3 text-gray-400">{c.agent_name || '—'}</td>
                      <td className={`p-3 font-mono text-right ${noContact ? 'text-red-400' : 'text-gray-400'}`}>
                        {lastContact}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan={6} className="p-3 text-center text-gray-500">No renewals due in {daysAhead} days.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// VIEW: Churn Analysis
// ===========================================================================
function ViewChurn({ range, onExport }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!range.start) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ start: range.start, end: range.end });
      const res  = await fetch(`${BASE}/api/reports/retention/churn?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <SectionHeader title="Churn Analysis" subtitle={`${range.start} to ${range.end}`} onExport={onExport} exportReport="churn" />
      <NarrativeBar text={data?.narrative} loading={loading} />

      <div className="grid grid-cols-2 gap-4 mb-6">
        <KpiTile label="Total Churned"  value={data?.churnCount ?? '—'} sub="this period" icon={TrendingDown} color="red"    loading={loading} />
        <KpiTile label="Churn Rate"     value={data?.churnRate != null ? `${data.churnRate}%` : '—'} sub="of active book" icon={Activity} color="amber" loading={loading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className={`${CARD} p-4`}>
          <h3 className="text-sm font-semibold text-white mb-4">Churn by Month</h3>
          {loading ? <Spinner /> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data?.byPeriod || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 10 }} tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="cnt" name="Churned" fill="#EF4444" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={`${CARD} p-4`}>
          <h3 className="text-sm font-semibold text-white mb-4">Churn by Carrier</h3>
          {loading ? <Spinner /> : (
            <div className="space-y-2">
              {(data?.byCarrier || []).map((c, i) => (
                <div key={c.carrier} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full flex-shrink-0 bg-red-400" />
                  <span className="flex-1 text-xs text-gray-200 truncate">{c.carrier}</span>
                  <span className="font-mono text-xs text-red-400">{c.cnt}</span>
                </div>
              ))}
              {!data?.byCarrier?.length && <p className="text-xs text-gray-500">No churn data for this period.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// VIEW: Agent Scorecards
// ===========================================================================
function ViewScorecards({ range, onExport }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!range.start) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ start: range.start, end: range.end });
      const res  = await fetch(`${BASE}/api/reports/agents/scorecards?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <SectionHeader title="Agent Scorecards" subtitle={`${range.start} to ${range.end}`} onExport={onExport} exportReport="scorecards" />

      {loading ? <Spinner /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(data?.agents || []).map((a, i) => (
            <div key={a.id} className={`${CARD} p-4`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      i === 0 ? 'bg-amber-400 text-gray-900' :
                      i === 1 ? 'bg-gray-300 text-gray-900' :
                      i === 2 ? 'bg-amber-700 text-white' : 'bg-gray-700 text-gray-400'
                    }`}>{i + 1}</span>
                    <h3 className="text-sm font-bold text-white">{a.name}</h3>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{a.email}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded font-mono ${
                  a.retention_rate >= 90 ? 'bg-emerald-400/20 text-emerald-400' :
                  a.retention_rate >= 75 ? 'bg-amber-400/20 text-amber-400' :
                  'bg-red-400/20 text-red-400'
                }`}>
                  {a.retention_rate}% ret.
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Enrollments</p>
                  <p className="text-xl font-bold font-mono text-amber-400">{a.enrollments}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Comms Sent</p>
                  <p className="text-xl font-bold font-mono text-blue-400">{parseInt(a.comms_sent || 0)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Tasks Done</p>
                  <p className="text-xl font-bold font-mono text-emerald-400">{parseInt(a.tasks_completed || 0)}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-800 flex items-center justify-between">
                <span className="text-xs text-gray-500">Active clients</span>
                <span className="font-mono text-xs text-gray-300">{a.active_clients}</span>
              </div>
            </div>
          ))}
          {!data?.agents?.length && (
            <div className={`${CARD} p-6 text-center col-span-2`}>
              <p className="text-gray-500 text-sm">No agent data found for this period.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// VIEW: Leaderboard
// ===========================================================================
function ViewLeaderboard({ range, onExport }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [metric, setMetric]   = useState('enrollments');

  const load = useCallback(async () => {
    if (!range.start) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ start: range.start, end: range.end, metric });
      const res  = await fetch(`${BASE}/api/reports/agents/leaderboard?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [range, metric]);

  useEffect(() => { load(); }, [load]);

  const max = data?.agents?.[0]?.[metric === 'comms' ? 'comms_sent' : 'enrollments'] || 1;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-white">Agent Leaderboard</h2>
          <p className="text-sm text-gray-400">{range.start} to {range.end}</p>
        </div>
        <div className="flex gap-2">
          {[['enrollments','Enrollments'],['comms','Communications']].map(([k, lbl]) => (
            <button key={k} onClick={() => setMetric(k)}
              className={`px-3 py-1 rounded text-xs font-medium ${
                metric === k ? 'bg-amber-400 text-gray-900' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <div className={`${CARD} overflow-hidden`}>
        {loading ? <Spinner /> : (
          <div className="divide-y divide-gray-800">
            {(data?.agents || []).map((a) => {
              const val = metric === 'comms' ? a.comms_sent : a.enrollments;
              const pct = max > 0 ? Math.round((val / max) * 100) : 0;
              return (
                <div key={a.id} className="p-4 flex items-center gap-4 hover:bg-gray-800/30 transition-colors">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    a.rank === 1 ? 'bg-amber-400 text-gray-900' :
                    a.rank === 2 ? 'bg-gray-300 text-gray-900' :
                    a.rank === 3 ? 'bg-amber-700 text-white' : 'bg-gray-700 text-gray-400'
                  }`}>
                    {a.rank === 1 ? '🥇' : a.rank === 2 ? '🥈' : a.rank === 3 ? '🥉' : a.rank}
                  </span>
                  <span className="w-32 text-sm text-gray-200 truncate">{a.agent_name}</span>
                  <div className="flex-1">
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-amber-400 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <span className="font-mono text-sm text-amber-400 font-bold w-12 text-right">{val}</span>
                </div>
              );
            })}
            {!data?.agents?.length && (
              <p className="p-4 text-center text-gray-500 text-sm">No data for this period.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// VIEW: Missing Commission Detector
// ===========================================================================
function ViewMissingCommission({ onExport }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${BASE}/api/reports/commissions/missing`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalFormatted = data?.totalGap != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(data.totalGap)
    : '—';

  return (
    <div>
      <SectionHeader title="Missing Commission Detector" subtitle="Cross-reference enrollments vs. commission records" />
      <NarrativeBar text={data?.narrative} loading={loading} />

      {!loading && !data?.hasCommissionSystem && (
        <div className="bg-blue-400/10 border border-blue-400/30 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-300 font-medium">Commission system not connected</p>
              <p className="text-xs text-blue-400 mt-1">
                The table below shows active clients enrolled more than 90 days ago with no commission records on file.
                Set up commission tracking in the Commissions module to enable precise gap detection.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-6">
        <KpiTile
          label="Gaps Detected"
          value={data?.gaps?.length ?? '—'}
          sub={data?.hasCommissionSystem ? 'active enrollments, no commission entry' : 'no commission records on file'}
          icon={AlertTriangle}
          color="red"
          loading={loading}
        />
        <KpiTile
          label="Estimated Recovery"
          value={totalFormatted}
          sub="based on avg commission rates"
          icon={DollarSign}
          color="amber"
          loading={loading}
        />
      </div>

      <div className={`${CARD} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left p-3 text-gray-400 font-medium">Client</th>
                <th className="text-left p-3 text-gray-400 font-medium">Carrier</th>
                <th className="text-left p-3 text-gray-400 font-medium">Plan</th>
                <th className="text-right p-3 text-gray-400 font-medium">Enrolled</th>
                <th className="text-left p-3 text-gray-400 font-medium">Agent</th>
                <th className="text-right p-3 text-gray-400 font-medium">Est. Gap</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6}><Spinner /></td></tr>
              ) : data?.gaps?.length ? (
                data.gaps.map((g, i) => (
                  <tr key={g.id || i} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="p-3 text-gray-200">{g.first_name} {g.last_name}</td>
                    <td className="p-3 text-gray-400">{g.carrier || '—'}</td>
                    <td className="p-3 text-gray-400">{g.plan_type || '—'}</td>
                    <td className="p-3 font-mono text-right text-gray-400">
                      {g.enrollment_date ? new Date(g.enrollment_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-3 text-gray-400">{g.agent_name || '—'}</td>
                    <td className="p-3 font-mono text-right text-red-400 font-bold">
                      ${g.gap != null ? Math.round(g.gap).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="p-6 text-center">
                    <CheckCircle size={24} className="text-emerald-400 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No missing commission entries detected.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// VIEW: Commission Earned by Period
// ===========================================================================
function ViewCommissionEarned({ range, onExport }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!range.start) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ start: range.start, end: range.end });
      const res  = await fetch(`${BASE}/api/reports/commissions/earned?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const totalFormatted = data?.total != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(data.total)
    : '—';

  return (
    <div>
      <SectionHeader title="Commission Earned by Period" subtitle={`${range.start} to ${range.end}`} />

      {!loading && !data?.hasCommissionSystem && (
        <div className="bg-amber-400/10 border border-amber-400/30 rounded-lg p-4 mb-6">
          <p className="text-sm text-amber-300">
            Commission tracking is not yet configured. Import your commission statements in the Commissions module to see earned data here.
          </p>
        </div>
      )}

      <KpiTile label="Total Earned" value={totalFormatted} sub="in selected period" icon={DollarSign} color="amber" loading={loading} />

      <div className={`${CARD} p-4 mt-4`}>
        {loading ? <Spinner /> : data?.rows?.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.rows} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 10 }} tickFormatter={v => v?.slice(5)} />
              <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="total" name="Commission ($)" fill="#F59E0B" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center text-gray-500 text-sm py-8">No commission data for this period.</p>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// VIEW: Book of Business
// ===========================================================================
function ViewBookOfBusiness() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${BASE}/api/reports/book/carrier-mix`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalClients = (data?.carriers || []).reduce((s, c) => s + parseInt(c.cnt), 0);

  return (
    <div>
      <SectionHeader title="Book of Business" subtitle="Current active client distribution" />

      <div className="mb-4">
        <KpiTile label="Total Active Clients" value={totalClients || '—'} icon={Users} color="amber" loading={loading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${CARD} p-4`}>
          <h3 className="text-sm font-semibold text-white mb-4">Carrier Mix</h3>
          {loading ? <Spinner /> : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={data?.carriers || []}
                    dataKey="cnt"
                    nameKey="carrier"
                    cx="50%" cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                  >
                    {(data?.carriers || []).map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {(data?.carriers || []).slice(0, 6).map((c, i) => (
                  <div key={c.carrier} className="flex items-center gap-2 text-xs">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="flex-1 text-gray-300 truncate">{c.carrier}</span>
                    <span className="font-mono text-gray-400">{c.cnt}</span>
                    <span className="font-mono text-gray-500 w-8 text-right">{c.pct}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className={`${CARD} p-4`}>
          <h3 className="text-sm font-semibold text-white mb-4">Plan Type Mix</h3>
          {loading ? <Spinner /> : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={data?.planTypes || []}
                    dataKey="cnt"
                    nameKey="plan_type"
                    cx="50%" cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                  >
                    {(data?.planTypes || []).map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {(data?.planTypes || []).slice(0, 6).map((p, i) => (
                  <div key={p.plan_type} className="flex items-center gap-2 text-xs">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="flex-1 text-gray-300">{p.plan_type}</span>
                    <span className="font-mono text-gray-400">{p.cnt}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// WAR ROOM: Full-screen AEP/OEP live dashboard
// ===========================================================================
function WarRoom({ window: warWindow, onExit, onWindowChange }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const intervalRef           = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ window: warWindow });
      const res  = await fetch(`${BASE}/api/reports/war-room?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [warWindow]);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(intervalRef.current);
  }, [load]);

  const paceVsPrior = data?.priorYearTotal > 0
    ? Math.round(((data.projectedTotal - data.priorYearTotal) / data.priorYearTotal) * 100)
    : 0;
  const dayPct = data?.totalDays > 0 ? Math.round((data.daysPassed / data.totalDays) * 100) : 0;
  const totalPct = data?.projectedTotal > 0
    ? Math.round((data.totalEnrollments / data.projectedTotal) * 100)
    : 0;
  const maxCarrier = data?.carrierRace?.[0]?.enrollments || 1;

  return (
    <div className="fixed inset-0 bg-gray-950 z-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-gray-800">
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {['aep','oep'].map(w => (
              <button key={w} onClick={() => onWindowChange(w)}
                className={`px-4 py-1.5 rounded text-sm font-bold uppercase tracking-wider transition-colors ${
                  warWindow === w ? 'bg-amber-400 text-gray-900' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}>
                {w}
              </button>
            ))}
          </div>
          <div className="text-white font-bold text-lg">{data?.label || '—'}</div>
          {data && (
            <div className="text-gray-400 text-sm">
              Day {data.daysPassed} of {data.totalDays}
            </div>
          )}
          <div className="flex items-center gap-1 text-xs text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            LIVE
          </div>
        </div>
        <button onClick={onExit}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors">
          <X size={14} />
          Exit
        </button>
      </div>

      {/* Main content */}
      {loading && !data ? (
        <div className="flex-1 flex items-center justify-center">
          <RefreshCw size={32} className="text-amber-400 animate-spin" />
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-8">
          <div className="grid grid-cols-3 gap-8 h-full">
            {/* Left: Big counter + pacing */}
            <div className="flex flex-col gap-6">
              <div className={`${CARD} p-8 text-center flex-1`}>
                <p className="text-gray-400 text-sm uppercase tracking-widest mb-2">Total Enrollments</p>
                <div className="text-7xl font-black font-mono text-amber-400 mb-4">
                  {data?.totalEnrollments ?? '—'}
                </div>
                <div className="text-gray-400 text-sm mb-6">
                  Projected: <span className="text-white font-mono">{data?.projectedTotal ?? '—'}</span>
                </div>
                {/* Pacing bar */}
                <div className="mb-2">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Progress</span>
                    <span>{totalPct}%</span>
                  </div>
                  <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-amber-400 transition-all"
                      style={{ width: `${Math.min(totalPct, 100)}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Days elapsed</span>
                    <span>{dayPct}%</span>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-blue-400 transition-all"
                      style={{ width: `${Math.min(dayPct, 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className={`${CARD} p-4 text-center`}>
                <p className="text-gray-400 text-xs uppercase tracking-widest mb-1">vs Prior Year</p>
                <div className={`text-4xl font-black font-mono ${paceVsPrior >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {paceVsPrior >= 0 ? '+' : ''}{paceVsPrior}%
                </div>
                <p className="text-gray-500 text-xs mt-1">Prior: {data?.priorYearTotal ?? '—'}</p>
              </div>
            </div>

            {/* Center: Leaderboard */}
            <div className={`${CARD} p-6 flex flex-col`}>
              <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <Award size={14} className="text-amber-400" />
                Leaderboard
              </h2>
              <div className="flex-1 space-y-3">
                {(data?.agentLeaderboard || []).map(a => (
                  <div key={a.id} className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      a.rank === 1 ? 'bg-amber-400 text-gray-900' :
                      a.rank === 2 ? 'bg-gray-300 text-gray-900' :
                      a.rank === 3 ? 'bg-amber-700 text-white' : 'bg-gray-700 text-gray-400'
                    }`}>
                      {a.rank <= 3 ? ['🥇','🥈','🥉'][a.rank - 1] : a.rank}
                    </span>
                    <span className="flex-1 text-white font-medium text-sm truncate">{a.name}</span>
                    <span className="font-mono text-2xl font-black text-amber-400">{a.enrollments}</span>
                  </div>
                ))}
                {!data?.agentLeaderboard?.length && (
                  <p className="text-gray-500 text-sm text-center pt-4">No enrollment data yet.</p>
                )}
              </div>
            </div>

            {/* Right: Carrier race */}
            <div className={`${CARD} p-6 flex flex-col`}>
              <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <Target size={14} className="text-amber-400" />
                Carrier Race
              </h2>
              <div className="flex-1 space-y-4">
                {(data?.carrierRace || []).map((c, i) => {
                  const pct = Math.round((c.enrollments / maxCarrier) * 100);
                  return (
                    <div key={c.carrier}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-200 font-medium truncate">{c.carrier}</span>
                        <span className="font-mono text-amber-400 font-bold ml-2">{c.enrollments}</span>
                      </div>
                      <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                        />
                      </div>
                    </div>
                  );
                })}
                {!data?.carrierRace?.length && (
                  <p className="text-gray-500 text-sm text-center pt-4">No carrier data yet.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
